import { observer } from "mobx-react";
import React from "react";
import { FontBody2 } from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../components/inputs/input/input";
import { IOption } from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { DROPDOWN_MENU_WIDTH, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { LABEL_POSITIONS, Toggle } from "../../../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import { AMOUNT_TYPE, FEE_TYPE, FEE_TYPE_FREE } from "../../../../policies/policy-types";
import { rootContainer, TYPE } from "../../../../../services/container/root";
import CurrencyService from "../../../../../services/currency-service";
import currentPortalStore from "../../../../../stores/current-portal-store";
import "../../../../create-rma/components/fees/rma-fees-card.scss";
import CRmaFeeEditorStore from "../../../../create-rma/stores/create-rma-fee-editor-store";

const createRmaScope = "dashboard-create-rma";
const settingsScope = "dashboard-settings-store";
interface IProps {
    returnFeeEditorStore: CRmaFeeEditorStore;
    labelFeeEditorStore: CRmaFeeEditorStore;
    validationGroup: IValidationGroup;
}

const CreateRmaDefaultFeesSection: React.FC<IProps> = observer(({ returnFeeEditorStore, labelFeeEditorStore, validationGroup }) => {
    const currencyService = rootContainer.get<CurrencyService>(TYPE.CurrencyService);
    const { getTranslation } = useDashboardTranslation();

    const returnFee = returnFeeEditorStore.getUpdatedModel();
    const labelFee = labelFeeEditorStore.getUpdatedModel();

    const returnFeeAmountOptions = returnFeeEditorStore.returnFeeAmountTypeOptions();
    const labelFeeAmountOptions = labelFeeEditorStore.labelFeeAmountTypeOptions();
    const labelFeeTypeOptions = labelFeeEditorStore.allShippingLabelFeeOptions();

    const returnHasPct = returnFeeEditorStore.usesPercentageAmount();

    const labelShowAmount = labelFeeEditorStore.showsAmountInputs();
    const labelHasEditable = labelFeeEditorStore.allowsAmountEdit();
    const labelHasPct = labelFeeEditorStore.usesPercentageAmount();

    return (
        <>
            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                {getTranslation("CREATE_RMA_SETTINGS_RMA_FEES_DEFAULTS", settingsScope)}
            </FontBody2>

            <FormFieldLine
                label={getTranslation("RETURN_FEE", createRmaScope)}
                dataName="create-rma-settings-default-return-fee"
            >
                <div className="rma-fees-card">
                    <div className="indent">
                        <div className="opened-editor">
                                <SelectDropdown
                                    value={returnFeeEditorStore.isCharged ? returnFeeAmountOptions.find((o) => o.value === returnFee.amount_type) : returnFeeAmountOptions.find((o) => o.value === FEE_TYPE_FREE)}
                                    options={returnFeeAmountOptions}
                                    menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
                                    onChange={(v) => returnFeeEditorStore.setReturnFeeAmountType(v?.value as AMOUNT_TYPE)}
                                    required
                                    validationGroup={validationGroup}
                                />
                                {returnFeeEditorStore.isCharged && ( <>
                                        {!returnHasPct &&
                                            currentPortalStore.setting?.currencyCode &&
                                            currencyService.getCurrencySymbol(currentPortalStore.setting?.currencyCode)}
                                        <Input
                                            value={returnFee.amount}
                                            onChange={(v) => returnFeeEditorStore.setAmount(v)}
                                            type={"number"}
                                            min={0}
                                            required
                                            validationGroup={validationGroup}
                                            disabled={!returnFeeEditorStore.isCharged}
                                        />
                                        {returnHasPct && "%"}
                                    <div className="align-to-left">
                                        <Toggle
                                            label={getTranslation("CHARGE_TAX", createRmaScope)}
                                            checked={returnFee.charge_tax ?? false}
                                            onChange={(v) =>
                                                returnFeeEditorStore.setChargeTax(v ?? false)
                                            }
                                            labelPosition={LABEL_POSITIONS.LEFT}
                                            disabled={!returnFeeEditorStore.isCharged}
                                        />
                                    </div>
                                    </>
                                )}

                        </div>
                    </div>
                </div>
            </FormFieldLine>
            <FormFieldLine
                label={getTranslation("LABEL_FEE", createRmaScope)}
                dataName="create-rma-settings-default-label-fee"
            >
                <div className="rma-fees-card">
                    <div className="indent">
                        <div className="opened-editor">
                            <SelectDropdown
                                options={labelFeeTypeOptions as IOption[]}
                                onChange={(v) => labelFeeEditorStore.setFeeType(v?.value as FEE_TYPE)}
                                menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
                                value={ labelFeeEditorStore.isCharged ? labelFeeTypeOptions.find((item) => item.value === labelFee.fee_type) : labelFeeTypeOptions.find((item) => item.value === FEE_TYPE_FREE)}
                                required={true}
                                validationGroup={validationGroup}
                            />
                            {labelShowAmount && labelFeeEditorStore.isCharged && (
                                <>
                                    {labelHasEditable ? (
                                        <SelectDropdown
                                            value={labelFeeAmountOptions.find((o) => o.value === labelFee.amount_type)}
                                            options={labelFeeAmountOptions}
                                            menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
                                            onChange={(v) => labelFeeEditorStore.setAmountType(v?.value as AMOUNT_TYPE)}
                                            required
                                            validationGroup={validationGroup}
                                            disabled={!labelFeeEditorStore.isCharged}
                                        />
                                    ) : (
                                        <FontBody2>{getTranslation("FIXED_AMOUNT", createRmaScope)}</FontBody2>
                                    )}
                                    {!labelHasPct &&
                                        currentPortalStore.setting?.currencyCode &&
                                        currencyService.getCurrencySymbol(currentPortalStore.setting?.currencyCode)}
                                    <Input
                                        value={labelFee.amount}
                                        onChange={(v) => labelFeeEditorStore.setAmount(v)}
                                        type={"number"}
                                        min={0}
                                        required
                                        validationGroup={validationGroup}
                                        disabled={!labelFeeEditorStore.isCharged}
                                    />
                                    {labelHasPct && "%"}
                                </>
                            )}
                            {
                                labelFeeEditorStore.isCharged && ( <>
                                    <div className="align-to-left">
                                        <Toggle
                                            label={getTranslation("CHARGE_TAX", createRmaScope)}
                                            checked={labelFee.charge_tax ?? false}
                                            onChange={(v) => labelFeeEditorStore.setChargeTax(v ?? false)}
                                            labelPosition={LABEL_POSITIONS.LEFT}
                                            disabled={!labelFeeEditorStore.isCharged}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </FormFieldLine>
        </>
    );
});

export default CreateRmaDefaultFeesSection;
